<template>
  <div class="blood">
    <BloodMarkersTable
      ref="markersTable"
      :headers="bloodMarkersHeader"
      :markersData="localBloodMarkersItems"
      :readOnly="bloodData.isReadOnly"
      :currentEditingId="currentInput ? currentInput.marker.id : null"
      :activeRowIndex="activeRow"
      :activeGroupName="activeGroupName"
      :expandedRows="expandedRows"
      @search="searchMarker"
      @filter="filterGetBloodMarkers"
      @activate-input="renderInputTag"
      @update-marker="handleUpdate"
      @clear-marker="removeMarkerValue"
      @toggle-group="toggleRow"
    />
  </div>
</template>

<script>
import BloodMarkersTable from '@/components/layouts/blood/BloodMarkersTable.vue'
import { mapActions, mapState } from 'vuex'
import NumberMixin from '@/mixins/Number'
import { trackEvent } from "@/utils/analytics"

export default {
  name: 'blood',
  mixins: [ NumberMixin ],
  components: { BloodMarkersTable },
  data: () => ({
    currentInput: null,
    localBloodMarkersItems: null,
    activeRow: false,
    activeGroupName: '',
    expandedRows: [],
    bloodGroupingType: 0
  }),
  computed: {
    ...mapState({
      visit: state => state.visits?.visit,
      bloodMarkersItems: state => state.blood.bloodMarkersItems,
      bloodData: state => state.blood.bloodData
    }),
    bloodMarkersHeader () {
      return [
        { text: 'MARKER', value: 'marker', sortable: false, isIcon: true, icon: 'mdi-magnify', isSlotable: true, customFilter: true, action: true, tag: 'input', width: '40%' },
        { text: 'VALUE', value: 'value', sortable: false, isSlotable: true, width: '15%' },
        { text: 'OPTIMAL RANGE', value: 'optimalRange', sortable: false, isSlotable: true, width: '15%' },
        { text: 'LAB RANGE', value: 'labRange', sortable: false, isSlotable: true, width: '15%' },
        { text: 'UNITS', value: 'units', sortable: false, isSlotable: true, width: '15%' }
      ]
    },
  },
  mounted() {
    this.initData()
  },
  methods: {
    ...mapActions('blood', ['getBloodMarkers', 'updateBloodMarkers']),

    initData() {
      this.getBloodMarkers({ id: this.visit.bloodTestId, groupingType: this.bloodGroupingType }).then(res => {
        if (res) {
          this.localBloodMarkersItems = this.bloodMarkersItems
          this.$set(this, 'expandedRows', this.localBloodMarkersItems.map(itemGroup => itemGroup.name))
        }
      })
    },
    renderInputTag({ marker, group, index }) {
      if (this.bloodData.isReadOnly) return
      this.activeRow = index
      this.activeGroupName = group.name
      this.currentInput = { marker, itemGroup: group, markerIndex: index }

      this.$nextTick(() => {
        if (this.$refs.markersTable && this.$refs.markersTable.focusInput) {
          this.$refs.markersTable.focusInput()
        }
      })
    },
    async handleUpdate({ value, next, prev, arrowUp, arrowDown, close }) {
      if (!this.currentInput) return

      const finalValue = (value !== undefined && value !== null) ? value : this.currentInput.marker.value
      await this.updateMarker(this.currentInput.marker.id, finalValue)

      if (next || arrowDown) {
        this.moveToNextRow()
      } else if (prev || arrowUp) {
        this.moveToPrevRow()
      } else if (close) {
        this.removeInputFromTable()
      }
    },
    async updateMarker(markerId, value) {
      const dataToSend = {
        bloodTestId: this.visit.bloodTestId,
        markerId,
        value
      }
      const { totalBloodEnteredValues } = await this.updateBloodMarkers({ id: this.visit.bloodTestId, params: dataToSend })
      await this.$store.dispatch('visits/updateTotalBloodEnteredValues', { totalBloodEnteredValues })
    },
    moveToNextRow() {
      if (!this.currentInput) return
      const markers = this.currentInput.itemGroup.markers.$values || this.currentInput.itemGroup.markers
      const currentIndex = this.currentInput.markerIndex

      if (currentIndex < markers.length - 1) {
        this.renderInputTag({
          marker: markers[currentIndex + 1],
          group: this.currentInput.itemGroup,
          index: currentIndex + 1
        })
      } else {
        const currentGroupIndex = this.localBloodMarkersItems.findIndex(g => g.name === this.currentInput.itemGroup.name)
        if (currentGroupIndex < this.localBloodMarkersItems.length - 1) {
          const nextGroup = this.localBloodMarkersItems[currentGroupIndex + 1]
          if (!this.expandedRows.includes(nextGroup.name)) this.expandedRows.push(nextGroup.name)
          const nextMarkers = nextGroup.markers.$values || nextGroup.markers
          this.renderInputTag({ marker: nextMarkers[0], group: nextGroup, index: 0 })
        }
      }
    },
    moveToPrevRow() {
      if (!this.currentInput) return

      const currentIndex = this.currentInput.markerIndex

      if (currentIndex > 0) {
        const markers = this.currentInput.itemGroup.markers.$values || this.currentInput.itemGroup.markers
        this.renderInputTag({
          marker: markers[currentIndex - 1],
          group: this.currentInput.itemGroup,
          index: currentIndex - 1
        })
      } else {
        const currentGroupIndex = this.localBloodMarkersItems.findIndex(g => g.name === this.currentInput.itemGroup.name)
        if (currentGroupIndex > 0) {
          const prevGroup = this.localBloodMarkersItems[currentGroupIndex - 1]
          if (!this.expandedRows.includes(prevGroup.name)) this.expandedRows.push(prevGroup.name)
          const prevMarkers = prevGroup.markers.$values || prevGroup.markers
          const lastIdx = prevMarkers.length - 1
          this.renderInputTag({ marker: prevMarkers[lastIdx], group: prevGroup, index: lastIdx })
        }
      }
    },
    removeInputFromTable() {
      this.currentInput = null
      this.activeRow = false
      this.activeGroupName = ''
    },
    toggleRow(key) {
      const index = this.expandedRows.indexOf(key)
      if (index > -1) this.expandedRows.splice(index, 1)
      else this.expandedRows.push(key)
    },
    searchMarker(value) {
      if (!value) {
        this.localBloodMarkersItems = this.bloodMarkersItems
      } else {
        let arr = this.localBloodMarkersItems.map(group => {
          return {
            ...group,
            markers: {
              $values: group.markers.$values.filter(marker => {
                return marker.markerName.toLowerCase().includes(value.toLowerCase())
              })
            }
          }
        })
        this.localBloodMarkersItems = arr

        trackEvent(this, 'search_marker', 'Blood', 'Search by Marker', {
          marker: value
        })
      }
    },
    filterGetBloodMarkers(val) {
      this.bloodGroupingType = val
      this.initData()
    },
    removeMarkerValue(markerId) {
      this.updateMarker(markerId, '')
      this.currentInput = null
      this.activeRow = false
    },
  }
}
</script>
